import { TaskOutcomeFieldsSchema } from '@erp/shared-types';
import type { TaskTypeResponse } from '@erp/rpc';
import {
  editorStateFor,
  isDefaultAssigneeComplete,
  isEditorDirty,
  isEscalationTargetComplete,
  parseActionDrafts,
  parseProcedureDrafts,
  type TaskTypeEditorState,
} from '../components/cards/task-types/taskTypeAdmin';
import { parseViewDraft } from '../components/cards/task-types/taskTypeViewDraft';
import {
  areMetricDraftsValid,
  type MetricFieldOption,
} from '../components/cards/task-types/taskTypeMetricsDraft';

export function resolveTaskTypeEditor(
  draft: TaskTypeEditorState | null,
  taskType: TaskTypeResponse | null | undefined,
): TaskTypeEditorState | null {
  return draft ?? (taskType ? editorStateFor(taskType) : null);
}

export function isTaskTypeEditorDirty(
  editor: TaskTypeEditorState | null,
): boolean {
  return editor !== null && isEditorDirty(editor);
}

export function canSaveTaskTypeEditor({
  canManage,
  editor,
  metricFields = null,
}: {
  canManage: boolean;
  editor: TaskTypeEditorState | null;
  /** Fields sum cards may total; null while the input schema is unknown. */
  metricFields?: readonly MetricFieldOption[] | null;
}): boolean {
  const parsedActions = editor ? parseActionDrafts(editor.actions) : null;
  const parsedProcedures = editor
    ? parseProcedureDrafts(editor.procedures)
    : null;
  return Boolean(
    canManage &&
    editor &&
    editor.name.trim() &&
    TaskOutcomeFieldsSchema.safeParse(editor.outcomeFields).success &&
    parsedActions &&
    parsedActions.ok &&
    parsedProcedures &&
    parsedProcedures.ok &&
    isDefaultAssigneeComplete(editor) &&
    isEscalationTargetComplete(editor) &&
    parseViewDraft(editor.viewText).ok &&
    areMetricDraftsValid(editor.metrics, metricFields),
  );
}

export function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
